import { useState, useRef, useEffect, useCallback } from "react";
import Select from "react-select";
import PropTypes from "prop-types";
import api from "api/api";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import Loader from "../../common/Loader";

// redux
import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";
import { loadCorrelationToggleState, loadSelectedCorrelationMethodState, loadSelectedCorrelationTargetVariableState, loadCorrelationStudyTypeMethodState } from "store/features/matchNetSlice";

import VariableCategoryColorMap from "../../../utils/VariableCategoryColorMap";
import { consumerFilterPayload } from "../../../utils/consumerFilterUtils";


// ─────────────────────────────────────────────
// Hardcoded Correlation Type options
// ─────────────────────────────────────────────
const CORRELATION_TYPE_OPTIONS = [
    { value: "kendall",  label: "Kendall Tau Correlation" },
    { value: "spearman", label: "Spearman Correlation"    },
];

const CORRELATION_COLOR_OVERRIDES = {
    "KPI(OO)": "#FA78CF",                       
    "Secondary Attribute": "#D8C70A",           
    "Product Preference Analysis": "#AD16CC",  
    "Fragrance Metrics Analysis": "#F47C31",   
};

// ─────────────────────────────────────────────
// Format option label
// displayLabel + macroCategory (colored)
// ─────────────────────────────────────────────
const formatOptionLabel = ({ label, tag }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ color: "#1c1c1c" }}>{label}</span>
        {tag && (
            <span
                style={{
                    fontSize   : 11,
                    fontStyle  : "italic",
                    fontWeight : 600,
                    color      : CORRELATION_COLOR_OVERRIDES[tag] || VariableCategoryColorMap?.[tag] || "#FA78CF",
                }}
            >
                {tag}
            </span>
        )}
    </div>
);

// ─────────────────────────────────────────────
// Shared react-select styles
// ─────────────────────────────────────────────
const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderRadius : 8,
        borderColor  : state.isFocused ? "#335eea" : "#d7dce5",
        boxShadow    : "none",
        minHeight    : 42,
        "&:hover"    : { borderColor: "#335eea" },
    }),
    menuPortal : (base) => ({ ...base, zIndex: 9999 }),
    menu       : (base) => ({ ...base, zIndex: 9999, fontSize: "0.9rem" }),
    menuList   : (base) => ({ ...base, maxHeight: "220px" }),
    option: (base, state) => ({
        ...base,
        backgroundColor : state.isSelected
            ? "#dce4fb"
            : state.isFocused
            ? "#eef1fc"
            : "#fff",
        color           : "#1c1c1c",
        cursor          : "pointer",
    }),
    singleValue : (base) => ({ ...base, fontWeight: 500 }),
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
function CorrelationDialogBox({
    isOpen                = false,
    setIsAddDialogOpen    = () => {},
    productSecondaryData  = [],
    variableMapping       = [],
    onApplyCorrelation    = () => {},
}) {
    const dispatch = useDispatch();

    const correlationSelectRef    = useRef(null);
    const targetVariableSelectRef = useRef(null);

    // ── Redux ──────────────────────────────────
    const filterData = useSelector((state) => state.matchNetStore.allFiltersCrossStudy);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const lookupSelectedProducts = useSelector((state) => state.matchNetStore.lookupSelectedProducts);
    const matchNetConsumerFilters = useSelector((state) => state.matchNetStore.consumerFilters);
    // console.log("lookupSelectedProducts", lookupSelectedProducts,variableMapping);

    // ── Build core_data_model_id, study_type, study_type_method ──
    // Same pattern as old CorrelationTab.fetchData()
    // but sourced from Cross Study Comparison's own props
    const coreDataModelIds = [
        ...new Set(
            productSecondaryData
                .map((d) => d.coreDataId)
                .filter((id) => id !== undefined && id !== null)
        ),
    ].join(",");

    const studyTypeIds = [
        ...new Set(
            variableMapping
                .map((group) => group.study_type)
                .filter((v) => v !== undefined && v !== null && v !== "")
        ),
    ].join(",");

    const studyTypeMethodIds = [
        ...new Set(
            variableMapping
                .map((group) => group.study_type_method)
                .filter((v) => v !== undefined && v !== null && v !== "")
        ),
    ].join(",");

    // ── State ──────────────────────────────────
    const [isLoading,             setIsLoading]             = useState(false);
    const [correlationType,       setCorrelationType]       = useState(CORRELATION_TYPE_OPTIONS[0]);
    const [targetVariable,        setTargetVariable]        = useState(null);
    const [targetVariableOptions, setTargetVariableOptions] = useState([]);
    const [fetchError,            setFetchError]            = useState("");
    const [products,              setProducts]              = useState([]);

    useEffect(() => {
        if (lookupSelectedProducts.length) {
            let tempProducts = [];
            lookupSelectedProducts.forEach((item) => {
                const obj = {
                    pc: item.title,
                    core_data_model_id: item.core_data_model 
                }
                tempProducts.push(obj);
            });
            setProducts(tempProducts);

        }
    }, [lookupSelectedProducts]);

    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    // ── Build payload — same shape as old CorrelationTab ──
    const buildPayload = useCallback(
        (targetVariableValue, methodToSend) => {
            let tempFilters = [];
            let tempMissingFilters = [];
            Object.keys(matchNetConsumerFilters).forEach((datum) => {
                const consumerFilter = matchNetConsumerFilters[datum]["currentSelectedFilters"] || {};
                const missingFilter = matchNetConsumerFilters[datum]["missingFilters"] || {}
                console.log("consumerFilter",consumerFilter)
                Object.keys(consumerFilter).forEach((productkeys) => {
                    const keyArr = productkeys.split("__");
                    const coreDataId = keyArr[keyArr.length - 1];
                    const productTitle = keyArr.slice(0, -1).join("__");
                    const filters = consumerFilterPayload(consumerFilter, productTitle, coreDataId);
                    if (Object.keys(filters).length) {
                        const obj = {
                            product_code: productTitle.split("_").join(" "),
                            core_data_model_id: coreDataId,
                            ...filters
                        }
                        tempFilters.push(obj);
                    }
                    const mfilters = consumerFilterPayload(missingFilter, productTitle, coreDataId);
                    if (Object.keys(mfilters).length) {
                        const obj = {
                            product_code: productTitle.split("_").join(" "),
                            core_data_model_id: coreDataId,
                            ...mfilters
                        }
                        tempMissingFilters.push(obj);
                    }
                })
            })
            console.log("tempFilters",tempFilters)
            // const consumerFilter = matchNetConsumerFilters[datum.study_type_method_title]["currentSelectedFilters"] || {};
            return {
                product_category         : parseInt(productCategoryId),
                core_data_model_id       : coreDataModelIds,
                study_type               : studyTypeIds,
                method                   : methodToSend,
                study_type_method        : studyTypeMethodIds,
                target_variable          : targetVariableValue,
                target_correlation_study_type_method : studyTypeMethodIds,
                weight_toggle            : false,
                products                 : products,
                applied_consumer_filters : tempFilters,
                applied_missing_filters  : tempMissingFilters
            }
        },
        [coreDataModelIds, productCategoryId, studyTypeIds, studyTypeMethodIds, products]
    );

    // ── Fetch target variable list (target_variable: null) ──
    // const fetchTargetVariableList = useCallback(async () => {
    //     if (!coreDataModelIds || !productCategoryId || !studyTypeIds || !studyTypeMethodIds) {
    //         setFetchError("Missing required study/product data.");
    //         return;
    //     }

    //     setIsLoading(true);
    //     setFetchError("");

    //     try {
    //         const url =
    //             process.env.REACT_APP_SERVER_PATH +
    //             "/secondary-study-datas/correlation/";

    //         const payload = buildPayload(null, correlationType.value);

    //         console.log("📤 correlation (list) payload →", payload);

    //         await api
    //             .post(url, payload)
    //             .then((res) => {
    //                 const _corrData = res.data["correlation_data"] || [];
    //                 console.log("📥 correlation_data →", _corrData);

    //                 if (_corrData.length > 0) {
    //                     const options = _corrData.map((item) => ({
    //                         value        : item.backendVariable,
    //                         label        : item.displayLabel?.charAt(0).toUpperCase() +
    //                                        item.displayLabel?.slice(1) || "",
    //                         displayLabel : item.displayLabel?.toLowerCase() || "",
    //                         tag          : item.macroCategory,
    //                     }));
    //                     setTargetVariableOptions(options);
    //                 } else {
    //                     setFetchError("No target variables returned.");
    //                 }
    //             })
    //             .catch((error) => {
    //                 handleError("error", error);
    //                 setFetchError(
    //                     error?.response?.data?.error ||
    //                     "Failed to fetch target variables."
    //                 );
    //             });
    //     } catch (error) {
    //         handleError("error", error);
    //         setFetchError("Failed to fetch target variables.");
    //     } finally {
    //         setIsLoading(false);
    //     }
    // }, [
    //     coreDataModelIds,
    //     productCategoryId,
    //     studyTypeIds,
    //     studyTypeMethodIds,
    //     buildPayload,
    //     correlationType.value,
    //     handleError,
    // ]);

    const fetchTargetVariableList = () => {
        let tempOptions = [];
        filterData.forEach((group) => {
            group.forEach((item) => {
                tempOptions.push(...item.displayLabels);
        });
        });
        const seen = new Set();
        const options = tempOptions
            .filter((item) => {
                if (seen.has(item.backendVariable)) return false;
                seen.add(item.backendVariable);
                return true;
            }).map((item) => ({
            value        : item.backendVariable,
            label        : item.displayLabel?.charAt(0).toUpperCase() +
                            item.displayLabel?.slice(1) || "",
            displayLabel : item.displayLabel?.toLowerCase() || "",
            tag          : item.variableCategory,
        }));
        setTargetVariableOptions(options);
    };

    // ── Fetch when dialog opens or method changes ──
    useEffect(() => {
        if (isOpen) {
            fetchTargetVariableList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, correlationType.value]);

    const handleCorrelationTypeChange = (newValue) => {
        setCorrelationType(newValue);
        setTargetVariable(null);
    };

    // ── Handlers ───────────────────────────────
    const handleClose = () => {
        setTargetVariable(null);
        setFetchError("");
        setIsAddDialogOpen(false);
    };

    // ── Get Results — final fetch with target_variable set ──
    const handleGetResults = async () => {
        if (!correlationType || !targetVariable) return;

        setIsLoading(true);
        try {
            const url =
                process.env.REACT_APP_SERVER_PATH +
                "/deodrants-match-net-analysis/correlation/";

            const payload = buildPayload(targetVariable.value, correlationType.value);

            console.log("📤 Get Results payload →", payload);

            await api
                .post(url, payload)
                .then((res) => {
                    // const _corrData = res.data["correlation_data"] || [];
                    const _corrData = res.data || [];

                    // ── Pass results up to CrossStudyComparisonTable ──
                    onApplyCorrelation({
                        correlationData        : _corrData,
                        targetVariable         : targetVariable,
                        correlationMethod      : correlationType,
                    });
                    
                    dispatch(loadCorrelationToggleState(true));
                    dispatch(loadSelectedCorrelationMethodState(correlationType.value));
                    dispatch(loadSelectedCorrelationTargetVariableState(targetVariable.value));
                    dispatch(loadCorrelationStudyTypeMethodState(payload.target_correlation_study_type_method));

                    setIsLoading(false);
                    handleClose();
                })
                .catch((error) => {
                    handleError("error", error);
                    setIsLoading(false);
                });
        } catch (error) {
            handleError("error", error);
            setIsLoading(false);
        }
    };

    // ─────────────────────────────────────────────
    return (
        <Dialog
            open={isOpen}
            fullWidth
            maxWidth="sm"
            aria-labelledby="customized-dialog-title"
            style={{ zIndex: 1300 }}
        >
            {isLoading && <Loader />}

            {/* ── Title ───────────────────────────── */}
            <div
                id="customized-dialog-title"
                style={{
                    backgroundColor : "rgba(51, 94, 234, 0.08)",
                    color           : "rgb(51, 94, 234)",
                    fontSize        : 14,
                    fontWeight      : 700,
                    letterSpacing   : 0.3,
                    textAlign       : "center",
                    padding         : "14px 40px",
                    position        : "relative",
                }}
            >
                PLEASE SELECT TYPE OF CORRELATION &amp; TARGET VARIABLE TO PROCEED
                <IconButton
                    aria-label="close dialog"
                    onClick={handleClose}
                    size="small"
                    sx={{
                        position : "absolute",
                        right    : 8,
                        top      : 6,
                        color    : "rgb(51, 94, 234) !important",
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </div>

            <DialogContent style={{ padding: "24px 32px 8px" }}>

                {/* ── Row 1 : Type of Correlation ─── */}
                <div style={{ marginBottom: 20 }}>
                    <label
                        htmlFor="correlation-type-select"
                        style={{
                            display      : "block",
                            marginBottom : 6,
                            color        : "rgb(51, 94, 234)",
                            fontWeight   : 600,
                            fontSize     : 14,
                        }}
                    >
                        Type of Correlation:
                    </label>
                    <Select
                        inputId="correlation-type-select"
                        ref={correlationSelectRef}
                        options={CORRELATION_TYPE_OPTIONS}
                        value={correlationType}
                        onChange={handleCorrelationTypeChange}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        isSearchable={false}
                        placeholder="Select correlation type..."
                    />
                </div>

                {/* ── Row 2 : Target Variable ─────── */}
                <div style={{ marginBottom: 12 }}>
                    <label
                        htmlFor="target-variable-select"
                        style={{
                            display      : "block",
                            marginBottom : 6,
                            color        : "rgb(51, 94, 234)",
                            fontWeight   : 600,
                            fontSize     : 14,
                        }}
                    >
                        Target Variable:
                    </label>
                    <Select
                        inputId="target-variable-select"
                        ref={targetVariableSelectRef}
                        options={targetVariableOptions}
                        value={targetVariable}
                        onChange={setTargetVariable}
                        formatOptionLabel={formatOptionLabel}
                        placeholder="Select target variable..."
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        isSearchable={true}
                        filterOption={(option, input) =>
                            !input ||
                            (option.data.displayLabel || "").includes(
                                input.toLowerCase()
                            )
                        }
                        noOptionsMessage={() =>
                            fetchError || "No variables available"
                        }
                    />

                    {fetchError && (
                        <p style={{
                            marginTop   : 6,
                            fontSize    : 12,
                            color       : "#d32f2f",
                            marginBottom: 0,
                        }}>
                             {fetchError}
                        </p>
                    )}
                </div>
            </DialogContent>

            {/* ── Actions ──────────────────────────── */}
            <DialogActions
                style={{ justifyContent: "center", padding: "16px 0 28px" }}
            >
                <Button
                    variant="outlined"
                    size="medium"
                    onClick={handleGetResults}
                    disabled={!correlationType || !targetVariable}
                    style={{
                        borderRadius  : 8,
                        borderColor   : !correlationType || !targetVariable
                            ? "#d7dce5"
                            : "rgb(51, 94, 234)",
                        color         : !correlationType || !targetVariable
                            ? "#aaa"
                            : "rgb(51, 94, 234)",
                        textTransform : "none",
                        fontWeight    : 600,
                        padding       : "6px 32px",
                    }}
                >
                    Get Results
                </Button>
            </DialogActions>
        </Dialog>
    );
}

CorrelationDialogBox.propTypes = {
    isOpen               : PropTypes.bool,
    setIsAddDialogOpen   : PropTypes.func,
    productSecondaryData : PropTypes.array,
    variableMapping      : PropTypes.array,
    onApplyCorrelation   : PropTypes.func,
};

export default CorrelationDialogBox;